import React, { useState, useMemo } from 'react';
import AlertRow from './AlertRow';
import SubjectSortMenu from './SubjectSortMenu';

/**
 * AlertTable Component - Displays alerts in a grouped table format
 * Groups are collapsible, with a table inside each group
 * Supports bulk actions via checkbox selection
 * Supports server-side sorting with clickable column headers
 */
function AlertTable({ groupedAlerts, groupBy, onViewDevice, onViewComplaint, onUpdateStatus, sorting, onSortChange, isRefreshing = false }) {
  // Track which groups are expanded
  const [expandedGroups, setExpandedGroups] = useState(
    Object.keys(groupedAlerts).reduce((acc, key) => ({ ...acc, [key]: true }), {})
  );

  // Track selected alert IDs per group: { groupKey: Set(alertIds) }
  const [selectedByGroup, setSelectedByGroup] = useState({});

  /**
   * Get sort indicator for column header (▲ for asc, ▼ for desc)
   */
  const getSortIndicator = (column) => {
    if (!sorting || sorting.sort_by !== column) return '';
    return sorting.sort_direction === 'asc' ? ' ▲' : ' ▼';
  };

  /**
   * Toggle group expansion state
   */
  const toggleGroup = (groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  /**
   * Toggle individual alert selection within a group
   */
  const toggleAlertSelection = (groupKey, alertId) => {
    setSelectedByGroup((prev) => {
      const groupSelection = prev[groupKey] ? new Set(prev[groupKey]) : new Set();
      if (groupSelection.has(alertId)) {
        groupSelection.delete(alertId);
      } else {
        groupSelection.add(alertId);
      }
      return {
        ...prev,
        [groupKey]: groupSelection.size > 0 ? groupSelection : undefined,
      };
    });
  };

  /**
   * Select/deselect all alerts in a specific group
   */
  const toggleSelectAllInGroup = (groupKey, groupAlerts) => {
    setSelectedByGroup((prev) => {
      const groupSelection = prev[groupKey];
      const isAllSelected = groupSelection && groupSelection.size === groupAlerts.length;

      if (isAllSelected) {
        // Deselect all
        return { ...prev, [groupKey]: undefined };
      } else {
        // Select all
        const newSelection = new Set(groupAlerts.map((alert) => alert.id));
        return { ...prev, [groupKey]: newSelection };
      }
    });
  };

  /**
   * Get all selected alert IDs across all groups
   */
  const getAllSelectedAlertIds = () => {
    const allSelected = new Set();
    Object.values(selectedByGroup).forEach((groupSelection) => {
      if (groupSelection) {
        groupSelection.forEach((id) => allSelected.add(id));
      }
    });
    return allSelected;
  };

  /**
   * Get statuses of all selected alerts
   */
  const getSelectedAlertStatuses = () => {
    const selectedIds = getAllSelectedAlertIds();
    const statuses = new Set();
    Object.values(groupedAlerts).forEach((groupAlerts) => {
      groupAlerts.forEach((alert) => {
        if (selectedIds.has(alert.id)) {
          statuses.add(alert.status);
        }
      });
    });
    return statuses;
  };

  /**
   * Determine which bulk actions are applicable based on selected alert statuses
   * Rules:
   * - Acknowledge: only if ALL selected are 'active'
   * - Snooze: only if ALL selected are 'active' or 'acknowledged'
   * - Unsnooze: only if ALL selected are 'snoozed'
   * - Archive: only if ALL selected are 'acknowledged' or 'snoozed' (not 'active' or 'archived')
   */
  const getApplicableActions = () => {
    const selectedStatuses = getSelectedAlertStatuses();

    return {
      canAcknowledge: selectedStatuses.size === 1 && selectedStatuses.has('active'),
      canSnooze: selectedStatuses.size > 0 &&
                 Array.from(selectedStatuses).every(s => s === 'active' || s === 'acknowledged') &&
                 !selectedStatuses.has('snoozed') &&
                 !selectedStatuses.has('archived'),
      canUnsnooze: selectedStatuses.size > 0 &&
                   Array.from(selectedStatuses).every(s => s === 'snoozed'),
      canArchive: selectedStatuses.size > 0 &&
                  Array.from(selectedStatuses).every(s => s === 'acknowledged' || s === 'snoozed'),
    };
  };

  // Count total selected alerts across all groups
  const totalSelectedAlerts = useMemo(() => {
    return getAllSelectedAlertIds().size;
  }, [selectedByGroup]);

  const applicableActions = useMemo(() => {
    return getApplicableActions();
  }, [selectedByGroup, groupedAlerts]);

  /**
   * Bulk acknowledge selected alerts
   */
  const handleBulkAcknowledge = () => {
    getAllSelectedAlertIds().forEach((alertId) => {
      onUpdateStatus(alertId, {
        status: 'acknowledged',
        acknowledged_at: new Date().toISOString(),
      });
    });
    setSelectedByGroup({});
  };

  /**
   * Bulk snooze selected alerts (7 days)
   */
  const handleBulkSnooze = () => {
    const snoozedUntil = new Date();
    snoozedUntil.setDate(snoozedUntil.getDate() + 7);
    getAllSelectedAlertIds().forEach((alertId) => {
      onUpdateStatus(alertId, {
        status: 'snoozed',
        snoozed_until: snoozedUntil.toISOString(),
      });
    });
    setSelectedByGroup({});
  };

  /**
   * Bulk archive selected alerts
   */
  const handleBulkArchive = () => {
    getAllSelectedAlertIds().forEach((alertId) => {
      onUpdateStatus(alertId, { status: 'archived' });
    });
    setSelectedByGroup({});
  };

  /**
   * Bulk unsnooze selected alerts (return to active)
   */
  const handleBulkUnsnooze = () => {
    getAllSelectedAlertIds().forEach((alertId) => {
      onUpdateStatus(alertId, {
        status: 'active',
        snoozed_until: null,
      });
    });
    setSelectedByGroup({});
  };

  /**
   * Clear all selections
   */
  const handleClearAllSelections = () => {
    setSelectedByGroup({});
  };

  return (
    <div className="alerts-table-container">
      {isRefreshing && (
        <div className="alerts-table-refresh-overlay" aria-busy="true" aria-live="polite">
          <div className="alerts-table-refresh-content">
            <div className="alerts-table-refresh-spinner" />
            <span>Refreshing alerts...</span>
          </div>
        </div>
      )}
      {/* Bulk Actions Toolbar - Shows when items selected */}
      {totalSelectedAlerts > 0 && (
        <div className="bulk-actions-toolbar">
          <div className="bulk-actions-info">
            <span className="bulk-count">{totalSelectedAlerts} selected</span>
            <button
              className="bulk-clear-btn"
              onClick={handleClearAllSelections}
              title="Clear selection"
            >
              Clear
            </button>
          </div>
          <div className="bulk-action-buttons">
            <button
              className="bulk-action-btn bulk-acknowledge"
              onClick={handleBulkAcknowledge}
              disabled={!applicableActions.canAcknowledge}
              title={applicableActions.canAcknowledge ? "Acknowledge selected alerts" : "Only available for active alerts"}
              style={{ opacity: applicableActions.canAcknowledge ? 1 : 0.5, cursor: applicableActions.canAcknowledge ? 'pointer' : 'not-allowed' }}
            >
              ✓ Acknowledge
            </button>
            <button
              className="bulk-action-btn bulk-snooze"
              onClick={handleBulkSnooze}
              disabled={!applicableActions.canSnooze}
              title={applicableActions.canSnooze ? "Snooze selected alerts for 7 days" : "Only available for active or acknowledged alerts"}
              style={{ opacity: applicableActions.canSnooze ? 1 : 0.5, cursor: applicableActions.canSnooze ? 'pointer' : 'not-allowed' }}
            >
              ⏰ Snooze 7d
            </button>
            <button
              className="bulk-action-btn bulk-unsnooze"
              onClick={handleBulkUnsnooze}
              disabled={!applicableActions.canUnsnooze}
              title={applicableActions.canUnsnooze ? "Return selected alerts to Active" : "Only available for snoozed alerts"}
              style={{ opacity: applicableActions.canUnsnooze ? 1 : 0.5, cursor: applicableActions.canUnsnooze ? 'pointer' : 'not-allowed' }}
            >
              ↩ Return to Active
            </button>
            <button
              className="bulk-action-btn bulk-archive"
              onClick={handleBulkArchive}
              disabled={!applicableActions.canArchive}
              title={applicableActions.canArchive ? "Archive selected alerts" : "Must acknowledge or snooze before archiving"}
              style={{ opacity: applicableActions.canArchive ? 1 : 0.5, cursor: applicableActions.canArchive ? 'pointer' : 'not-allowed' }}
            >
              🗂️ Archive
            </button>
          </div>
        </div>
      )}

      {Object.entries(groupedAlerts).map(([groupKey, groupAlerts]) => (
        <div key={groupKey} className="alert-group-section">
          {/* Group Header - Collapsible */}
          <div
            className="alert-group-header-table"
            onClick={() => toggleGroup(groupKey)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                toggleGroup(groupKey);
              }
            }}
          >
            <div className="group-header-content">
              <span
                className={`expand-icon ${expandedGroups[groupKey] ? 'expanded' : ''}`}
              >
                ▶️
              </span>
              <h3 className="group-title">{groupKey}</h3>
              <span className="group-count">({groupAlerts.length})</span>
            </div>
          </div>

          {/* Group Table - Collapsed/Expanded */}
          {expandedGroups[groupKey] && (
            <div className="alert-group-table-wrapper">
              <table className="alerts-table">
                <colgroup>
                  <col className="alert-table-col-checkbox" />
                  <col className="alert-table-col-severity" />
                  <col className="alert-table-col-type" />
                  <col className="alert-table-col-subject" />
                  <col className="alert-table-col-message" />
                  <col className="alert-table-col-status-date" />
                  {onUpdateStatus && <col className="alert-table-col-actions" />}
                </colgroup>
                <thead>
                  <tr className="alerts-table-header">
                    <th className="alert-table-col-checkbox" title="Select alert in this group">
                      <input
                        type="checkbox"
                        className="select-all-checkbox"
                        checked={
                          selectedByGroup[groupKey] &&
                          selectedByGroup[groupKey].size === groupAlerts.length
                        }
                        onChange={() => toggleSelectAllInGroup(groupKey, groupAlerts)}
                        title={`Select all alerts in ${groupKey}`}
                        aria-label={`Select all alerts in ${groupKey}`}
                      />
                    </th>
                    <th
                      className="alert-table-col-severity"
                      title="Click to sort by severity"
                      onClick={() => onSortChange && onSortChange('severity')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      Severity{getSortIndicator('severity')}
                    </th>
                    <th
                      className="alert-table-col-type"
                      title="Click to sort by alert type"
                      onClick={() => onSortChange && onSortChange('type')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      Type{getSortIndicator('type')}
                    </th>
                    <SubjectSortMenu
                      sorting={sorting}
                      onSortChange={onSortChange}
                      getSortIndicator={getSortIndicator}
                    />
                    <th className="alert-table-col-message" title="Alert message">Message</th>
                    <th
                      className="alert-table-col-status-date"
                      title="Click to sort by last detected"
                      onClick={() => onSortChange && onSortChange('last_detected_at')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      Status / Last detected{getSortIndicator('last_detected_at')}
                    </th>
                    {onUpdateStatus && (
                      <th className="alert-table-col-actions" title="Alert actions">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {groupAlerts.map((alert) => (
                    <AlertRow
                      key={alert.id}
                      alert={alert}
                      isSelected={
                        selectedByGroup[groupKey] && selectedByGroup[groupKey].has(alert.id)
                      }
                      onSelectChange={() => toggleAlertSelection(groupKey, alert.id)}
                      onViewDevice={onViewDevice}
                      onViewComplaint={onViewComplaint}
                      onUpdateStatus={onUpdateStatus}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default AlertTable;
